import { useEffect, useMemo, useState } from 'react'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'

export const _userNamesMap = new Map<number, string | null>()
/**
 * Given a userId, call the backend to get the user's name and cache it in a `Map` until the page is reloaded.
 *
 * Note: The `Map` is stored in a module-level variable, so it's shared across all instances of this hook
 *
 * Note: Will not call the backend again if a request has already been made for the same userId, even if the request has not
 * resolved yet
 * @param id a user ID
 * @param onUpdate callback that will be invoked when the userName for that userId is returned
 */
const fetchUserName = (id: number, onUpdate: (newUserMap: Map<number, string>) => void) => {
  if (_userNamesMap.has(id) && _userNamesMap.get(id) !== null) {
    onUpdate(mapWithoutNulls(_userNamesMap))
  } else if (!_userNamesMap.has(id)) {
    _userNamesMap.set(id, null)
    client.getUserCard(id).then((response) => {
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        const { display_name, first_name, last_name } = response.data.user
        _userNamesMap.set(id, display_name ?? `${first_name} ${last_name}`)
        onUpdate(mapWithoutNulls(_userNamesMap))
      } else if (_userNamesMap.get(id) === null) {
        // we received an error, so we should allow new requests to proceed. Maybe they'll succeed next time
        _userNamesMap.delete(id)
      }
    })
  }
}
/**
 * Given a `Map`, return a copy stripped of all keys that have `null` as values
 */
function mapWithoutNulls<K, T>(map: Map<K, T | null>): Map<K, T> {
  return new Map(Array.from(map.entries()).filter(([, value]) => value !== null) as Array<[K, T]>)
}

/**
 * A hook that returns a `Map` of user IDs to user names
 * @param userIDs An array or a set (arrays will be coerced into sets anyways) of user IDs you are interested in
 * @returns a `Map` of user IDs to user names. The `Map` will be updated as the user names are fetched, but changes to
 * the userNames in the backend will not be reflected in the `Map` returned by this hook until the page is refreshed
 */
export const useUserNames = (userIDs: number[] | Set<number>): Map<number, string> => {
  const userIdsSet = useMemo(() => (userIDs instanceof Set ? userIDs : new Set(userIDs)), [userIDs])

  const [userNamesMap, setUserNamesMap] = useState(new Map<number, string>())

  useEffect(() => {
    userIdsSet.forEach((id) => {
      if (!userNamesMap.has(id)) {
        fetchUserName(id, setUserNamesMap)
      }
    })
  }, [userIdsSet, userNamesMap])

  return userNamesMap
}
